import * as _ from 'underscore';
import { ServerStatusCtrl } from '../controllers/ServerStatusCtrl';
import { L10n } from '../L10n/L10n';
import { Module } from '../Module';
import {
  GetWorkerProcessStatus,
  IGetWorkerProcessStatusResult,
  WorkerStatusType,
} from '../ServerApi/requests/GetWorkerProcessStatus';
import { IWorkerProcess, WorkerProcessType } from '../ServerApi/types/IWorkerProcess';
import { HelpService } from '../services/HelpService';
import { ServerService } from '../services/ServerService';

interface IProcessStatusScope extends ng.IScope {
  process: IWorkerProcess;
  isPreferred: () => boolean;
  status: string;
  pending?: number;
  syncTimestamp?: string;
  serverStatusHelpUrl: string;
  showStatusText: () => boolean;
  statusText: () => string;
  statusTooltip: () => string;
  statusIconType: () => string;
}

Module.instance().directive('tbWorkerProcessStatus', <any[]>['$interval', '$translate','ServerService', 'HelpService',
  ($interval: ng.IIntervalService, $translate: ng.translate.ITranslateService, server: ServerService, help: HelpService) => {
    const PollMS = 60000;
    return {
      scope: {
        process: '=',
        isPreferred: '&'
      },
      restrict: 'E',
      replace: true,
      templateUrl: 'workerProcessStatus.html',
      link: ($scope: IProcessStatusScope) => {
        let refreshStatusInterval: ng.IPromise<any>;
        let scopeDestroyed = false;
        const refreshStatus = (): void => {
          if ($scope.process.isExternal) {
            // External Services don't monitor the status.
            $scope.status = WorkerStatusType.external;
            $scope.pending = 0;
            return;
          }
          server.sendRequest(new GetWorkerProcessStatus({process: $scope.process}))
            .then((result: IGetWorkerProcessStatusResult) => {
              if (scopeDestroyed) {
                // If the scope has been destroyed while the request was pending (user left the page)
                return;
              }
              $scope.status = result.status;
              $scope.pending = result.pendingTransfers;
              $scope.syncTimestamp = result.syncTimestamp;

              refreshStatusInterval = $interval(refreshStatus, PollMS, 1, false);
            });
        };
        refreshStatus();

        $scope.showStatusText = (): boolean => {
          const status: string = $scope.status;
          switch($scope.process.type) {
            case WorkerProcessType.clusterController:
              return status === WorkerStatusType.down;
            case WorkerProcessType.fileStore:
              return _.contains([
                  WorkerStatusType.activeSyncing,
                  WorkerStatusType.down,
                  WorkerStatusType.decommissioningReadOnly,
                  WorkerStatusType.decommissionFailedReadOnly,
                  WorkerStatusType.readyForRemoval
                ], status);
            case WorkerProcessType.repository:
              return status === WorkerStatusType.notAvailable;
            default:
              return false;
          }
        };

        $scope.statusText = (): string => {
          return $translate.instant($scope.process.type + '_' + $scope.status + '_status');
        };

        $scope.statusTooltip = (): string => {
          if ($scope.pending > 0 || $scope.status === WorkerStatusType.activeSyncing) {
            return $translate.instant('filestore_activesyncing_tooltip', { PENDING_COUNT: $scope.pending }, L10n.Interpolations.Count);
          } else {
            return $translate.instant($scope.process.type + '_' + $scope.status + '_tooltip');
          }
        };

        $scope.statusIconType = (): string => {
          switch($scope.status) {
            case WorkerStatusType.active:
            case WorkerStatusType.decommissionFailedReadOnly:
            case WorkerStatusType.readyForRemoval:
              return WorkerStatusType.active;
            case WorkerStatusType.passive:
            case WorkerStatusType.readOnly:
              return WorkerStatusType.passive;
            case WorkerStatusType.busy:
            case WorkerStatusType.activeSyncing:
            case WorkerStatusType.decommissioningReadOnly:
            case WorkerStatusType.notAvailable:
              return WorkerStatusType.busy;
            case WorkerStatusType.unlicensed:
            case WorkerStatusType.down:
            case WorkerStatusType.statusNotAvailable:
            case WorkerStatusType.external:
              return $scope.status;
          }
        };

        $scope.serverStatusHelpUrl = help.getServerHelpUrlByCategory('status1');

        $scope.$on(ServerStatusCtrl.refreshProcessStatusEvent, () => {
          $interval.cancel(refreshStatusInterval);
          $scope.status = undefined; // Show spinner if refresh is manually triggered
          refreshStatus();
        });

        $scope.$on('$destroy', () => {
          $interval.cancel(refreshStatusInterval);
          scopeDestroyed = true;
        });

      }
    };
  }
]);
